import { combineReducers, Reducer } from 'redux';
import { persistReducer } from 'redux-persist';

import { BULK_RECIPIENTS_THRESHOLD } from '../../constants/createSendRecipients';
import { ICreateSendState } from '../../types/createSend';
import { CreateSendMethodEnum } from '../../types/createSendFlow';
import { isRecipientComplete } from '../../utils/createSendRecipients';
import { SET_CS_METHOD } from './actions/method';
import {
  CS_ADD_RECIPIENT,
  CS_CLEAR_RECIPIENTS,
  CS_REMOVE_RECIPIENT,
  CS_SET_RECIPIENTS,
  CS_UPDATE_RECIPIENT,
} from './actions/recipients';
import { RESET_CREATE_SEND } from './actions/root';
import { createSendPersistConfig } from './persist';
import claimPage from './reducers/claimPage';
import contents from './reducers/contents';
import contentsAttempt from './reducers/contentsAttempt';
import crm from './reducers/crm';
import customMessage from './reducers/customMessage';
import email from './reducers/email';
import linkAction from './reducers/linkAction';
import linkSettings from './reducers/linkSettings';
import method from './reducers/method';
import recipients from './reducers/recipients';
import sendSettings from './reducers/sendSettings';
import shipmentInfo from './reducers/shipmentInfo';
import submit from './reducers/submit';

const combined = combineReducers<ICreateSendState>({
  method,
  contents,
  contentsAttempt,
  recipients,
  email,
  customMessage,
  shipmentInfo,
  sendSettings,
  linkSettings,
  linkAction,
  claimPage,
  crm,
  submit,
});

const isMethodChange = (state: ICreateSendState | undefined, action: { type: string; payload?: unknown }): boolean =>
  action.type === SET_CS_METHOD && (state?.method ?? null) !== action.payload;

const seedMethodDefaults = (state: ICreateSendState, nextMethod: unknown): ICreateSendState => ({
  ...state,
  sendSettings: { ...state.sendSettings, disableSenderEmails: nextMethod === CreateSendMethodEnum.Link },
});

const RECIPIENT_MUTATIONS: string[] = [
  CS_ADD_RECIPIENT,
  CS_UPDATE_RECIPIENT,
  CS_REMOVE_RECIPIENT,
  CS_CLEAR_RECIPIENTS,
  CS_SET_RECIPIENTS,
];

const countCompleteRecipients = (state: ICreateSendState | undefined): number =>
  (state?.recipients.recipients ?? []).filter(isRecipientComplete).length;

const isBecomingBulk = (state: ICreateSendState | undefined, next: ICreateSendState): boolean =>
  countCompleteRecipients(state) < BULK_RECIPIENTS_THRESHOLD &&
  countCompleteRecipients(next) >= BULK_RECIPIENTS_THRESHOLD;

const silenceSenderEmailsForBulk = (state: ICreateSendState): ICreateSendState =>
  state.sendSettings.isSenderEmailsTouched || state.sendSettings.disableSenderEmails
    ? state
    : { ...state, sendSettings: { ...state.sendSettings, disableSenderEmails: true } };

export const createSendReducer: Reducer<ICreateSendState> = (state, action) => {
  if (action.type === RESET_CREATE_SEND) {
    return combined(undefined, action);
  }

  if (isMethodChange(state, action)) {
    return seedMethodDefaults(combined(undefined, action), action.payload);
  }

  const next = combined(state, action);

  if (
    next.method === CreateSendMethodEnum.Recipients &&
    RECIPIENT_MUTATIONS.includes(action.type) &&
    isBecomingBulk(state, next)
  ) {
    return silenceSenderEmailsForBulk(next);
  }

  return next;
};

export const persistedCreateSendReducer = persistReducer<ICreateSendState>(createSendPersistConfig, createSendReducer);

export default persistedCreateSendReducer;
